import React from 'react';
import { AlertCircle, Moon, Activity, Droplets, Battery, Flame, Coffee, Scale } from 'lucide-react';
export function PainSection() {
  const symptoms = [{
    icon: <Moon className="text-forest" size={20} data-id="element-320" />,
    signal: 'Vientre duro al acostarte',
    meaning: 'Fermentación activa en el intestino'
  }, {
    icon: <Droplets className="text-forest" size={20} data-id="element-321" />,
    signal: 'Cara hinchada al despertar',
    meaning: 'Retención de líquidos por sodio oculto'
  }, {
    icon: <Battery className="text-forest" size={20} data-id="element-322" />,
    signal: 'Fatiga que no mejora con el sueño',
    meaning: 'Sistema inmune sobreactivado por inflamación'
  }, {
    icon: <Activity className="text-forest" size={20} data-id="element-323" />,
    signal: 'Antojo dulce tras cenar',
    meaning: 'Pico de insulina descontrolado'
  }, {
    icon: <Flame className="text-forest" size={20} data-id="element-324" />,
    signal: 'Reflujo o acidez nocturna',
    meaning: 'Digestión incompleta bajo estrés'
  }, {
    icon: <Scale className="text-forest" size={20} data-id="element-325" />,
    signal: 'Peso estancado a pesar de comer poco',
    meaning: 'Cortisol crónico bloqueando la quema de grasa'
  }];
  return <section className="py-16 md:py-24 px-4 bg-cream relative" data-id="element-326">
      <div className="max-w-3xl mx-auto" data-id="element-327">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-forest mb-8 text-center" data-id="element-328">
          La Mentira Que Te Han Contado Durante Años
        </h2>

        <div className="space-y-4 text-lg text-dark/80 mb-10" data-id="element-329">
          <p className="flex items-start gap-3" data-id="element-330">
            <span className="text-urgency font-bold mt-1" data-id="element-331">✕</span>
            <span data-id="element-332">
              Te han dicho que si comes menos y te mueves más, todo se
              soluciona.
            </span>
          </p>
          <p className="flex items-start gap-3" data-id="element-333">
            <span className="text-urgency font-bold mt-1" data-id="element-334">✕</span>
            <span data-id="element-335">
              Te han dicho que la ensalada de noche es lo más sano que puedes
              hacer.
            </span>
          </p>
          <p className="flex items-start gap-3" data-id="element-336">
            <span className="text-urgency font-bold mt-1" data-id="element-337">✕</span>
            <span data-id="element-338">
              Te han dicho que los productos "light" y "sin azúcar" son tus
              aliados.
            </span>
          </p>
        </div>

        <p className="text-xl font-bold text-dark mb-4 text-center" data-id="element-339">
          Y llevas años siguiendo ese consejo.
        </p>
        <p className="text-2xl font-serif font-bold text-forest mb-8 text-center" data-id="element-340">
          ¿Y sabes qué?
        </p>

        <p className="text-lg text-dark/80 mb-12 text-center max-w-2xl mx-auto" data-id="element-341">
          Sigues hinchada. Sigues cansada. Sigues despertándote con ese abdomen
          que no reconoces como tuyo.
        </p>

        <div className="bg-white p-6 md:p-10 rounded-2xl shadow-warm mb-12" data-id="element-342">
          <p className="text-lg mb-6" data-id="element-343">
            Porque lo que nadie te ha explicado es esto:
          </p>
          <p className="text-xl font-bold text-forest mb-6" data-id="element-344">
            A partir de los 35 años, tu cuerpo procesa los alimentos nocturnos
            de una forma completamente distinta.
          </p>

          <ul className="space-y-4 text-dark/80 mb-8 list-disc pl-5 marker:text-gold" data-id="element-345">
            <li data-id="element-346">
              Después de las 7 PM, tu producción de enzimas digestivas cae hasta
              un 40%.
            </li>
            <li data-id="element-347">Tu sensibilidad a la insulina disminuye drásticamente.</li>
            <li data-id="element-348">Tu motilidad intestinal se ralentiza.</li>
          </ul>

          <div className="space-y-4 text-dark/80 bg-cream-dark/30 p-6 rounded-xl border border-forest/10" data-id="element-349">
            <p data-id="element-350">
              Esto significa que la ensalada cruda que cenaste con tanta
              responsabilidad...{' '}
              <strong data-id="element-351">
                está fermentando en tu intestino mientras intentas dormir.
              </strong>
            </p>
            <p data-id="element-352">
              Que el yogur "sin grasa" está alterando tu microbiota con
              edulcorantes artificiales que la ciencia ya ha relacionado con{' '}
              <strong data-id="element-353">inflamación crónica.</strong>
            </p>
            <p data-id="element-354">
              Que el pan integral que elegiste sobre el pan blanco sigue
              disparando tu insulina nocturna y ordenando a tu cuerpo{' '}
              <strong data-id="element-355">almacenar grasa abdominal.</strong>
            </p>
          </div>
        </div>

        {/* Warning Callout */}
        <div className="bg-red-50 border-2 border-urgency/30 rounded-xl p-6 flex flex-col md:flex-row items-center gap-4 mb-16 shadow-sm" data-id="element-356">
          <div className="bg-white p-3 rounded-full shrink-0" data-id="element-357">
            <AlertCircle className="text-urgency" size={32} data-id="element-358" />
          </div>
          <p className="text-xl md:text-2xl font-bold text-urgency text-center md:text-left" data-id="element-359">
            No fallaste en la dieta. La dieta te falló a ti.
          </p>
        </div>

        <p className="text-lg text-dark/80 mb-8 text-center" data-id="element-360">
          Y mientras tanto, tu cuerpo lleva meses, quizás años, enviándote
          señales que no sabías cómo interpretar:
        </p>

        {/* Symptoms Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-forest/10 overflow-hidden mb-12" data-id="element-361">
          <div className="grid grid-cols-1 md:grid-cols-2 bg-forest text-white font-bold text-sm md:text-base" data-id="element-362">
            <div className="p-4 border-b md:border-b-0 md:border-r border-white/20" data-id="element-363">
              🔴 Señal
            </div>
            <div className="p-4" data-id="element-364">Lo que realmente significa</div>
          </div>
          <div className="divide-y divide-forest/10" data-id="element-365">
            {symptoms.map((item, index) => <div key={index} className={`grid grid-cols-1 md:grid-cols-2 text-sm md:text-base ${index % 2 === 0 ? 'bg-white' : 'bg-cream/50'}`} data-id="element-366">
                <div className="p-4 font-medium text-dark flex items-center gap-3 border-b md:border-b-0 md:border-r border-forest/10" data-id="element-367">
                  <span className="shrink-0 bg-cream-dark p-2 rounded-lg" data-id="element-368">
                    {item.icon}
                  </span>
                  {item.signal}
                </div>
                <div className="p-4 text-dark/80 flex items-center" data-id="element-369">
                  {item.meaning}
                </div>
              </div>)}
          </div>
        </div>

        {/* Emotional Closing */}
        <div className="text-center max-w-2xl mx-auto space-y-6" data-id="element-370">
          <p className="text-xl font-bold text-forest" data-id="element-371">
            Y lo peor de todo no es físico.
          </p>
          <p className="text-lg text-dark/80" data-id="element-372">
            Lo peor es esa sensación de traición.
          </p>
          <p className="text-lg text-dark/80" data-id="element-373">
            La sensación de hacer todo bien... y que tu cuerpo no responda.
          </p>
          <p className="text-lg text-dark/80" data-id="element-374">
            La frustración de mirarte en ese espejo cada mañana y no
            reconocerte.
          </p>
          <p className="text-lg text-dark/80" data-id="element-375">
            La fatiga emocional de llevar años luchando contra algo que no
            entiendes.
          </p>

          <div className="mt-10 inline-block" data-id="element-376">
            <span className="relative inline-block" data-id="element-377">
              <span className="absolute inset-x-0 bottom-2 h-3 bg-gold/40 -z-10 transform -rotate-1" data-id="element-378"></span>
              <span className="font-serif text-3xl md:text-4xl font-bold text-forest" data-id="element-379">
                💬 Eso termina hoy.
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>;
}